/**
 * Entity Sync Manager
 * 
 * Tracks entity versions and manages delta synchronization.
 * Handles version conflicts and ensures data consistency across clients.
 */

export interface EntityVersion {
  id: string;
  type: 'player' | 'npc' | 'ship' | 'resource' | 'custom';
  version: number;
  timestamp: number;
  data: any;
  hash: string;
}

export interface DeltaUpdate {
  entityId: string;
  version: number;
  changes: Record<string, any>;
  timestamp: number;
}

export interface ConflictResolution {
  strategy: 'server-wins' | 'client-wins' | 'merge';
  clientVersion: number;
  serverVersion: number;
  resolvedData: any;
}

export class EntitySyncManager {
  private static entities: Map<string, EntityVersion> = new Map();
  private static pendingDeltas: Map<string, DeltaUpdate[]> = new Map();
  private static conflictLog: ConflictResolution[] = [];

  /**
   * Register or update an entity version
   */
  static registerEntity(
    id: string,
    type: EntityVersion['type'],
    data: any,
    version: number = 1
  ): void {
    const hash = this.hashData(data);
    const entity: EntityVersion = {
      id,
      type,
      version,
      timestamp: Date.now(),
      data,
      hash,
    };

    this.entities.set(id, entity);
    // Suppress spam logs for frequently-updated entities
    // console.log(`📝 Entity registered: ${id} (v${version})`);
  }

  /**
   * Apply a delta update to an entity
   */
  static applyDelta(entityId: string, delta: DeltaUpdate): boolean {
    const entity = this.entities.get(entityId);

    if (!entity) {
      console.warn(`⚠️ Entity not found for delta: ${entityId}`);
      return false;
    }

    // Check version match
    if (delta.version !== entity.version + 1) {
      console.warn(
        `⚠️ Version mismatch for ${entityId}: expected ${entity.version + 1}, got ${delta.version}`
      );
      // Queue delta for later processing
      if (!this.pendingDeltas.has(entityId)) {
        this.pendingDeltas.set(entityId, []);
      }
      this.pendingDeltas.get(entityId)!.push(delta);
      return false;
    }

    // Apply changes
    const updatedData = { ...entity.data, ...delta.changes };
    entity.data = updatedData;
    entity.version = delta.version;
    entity.timestamp = delta.timestamp;
    entity.hash = this.hashData(updatedData);

    console.log(`✅ Delta applied to ${entityId} (v${delta.version})`);

    // Process any pending deltas
    this.processPendingDeltas(entityId);

    return true;
  }

  /**
   * Process pending deltas for an entity
   */
  private static processPendingDeltas(entityId: string): void {
    const deltas = this.pendingDeltas.get(entityId);
    if (!deltas || deltas.length === 0) return;

    const entity = this.entities.get(entityId);
    if (!entity) return;

    // Sort deltas by version
    deltas.sort((a, b) => a.version - b.version);

    // Apply deltas in order
    for (const delta of deltas) {
      if (delta.version === entity.version + 1) {
        this.applyDelta(entityId, delta);
      }
    }

    // Clear processed deltas
    this.pendingDeltas.delete(entityId);
  }

  /**
   * Detect and resolve version conflicts
   */
  static resolveConflict(
    entityId: string,
    clientVersion: number,
    serverVersion: number,
    serverData: any,
    strategy: 'server-wins' | 'client-wins' | 'merge' = 'server-wins'
  ): ConflictResolution {
    const entity = this.entities.get(entityId);
    if (!entity) {
      throw new Error(`Entity not found: ${entityId}`);
    }

    let resolvedData: any;

    switch (strategy) {
      case 'server-wins':
        resolvedData = serverData;
        entity.data = serverData;
        entity.version = serverVersion;
        console.log(`🔄 Conflict resolved (server-wins): ${entityId}`);
        break;

      case 'client-wins':
        resolvedData = entity.data;
        console.log(`🔄 Conflict resolved (client-wins): ${entityId}`);
        break;

      case 'merge':
        resolvedData = this.mergeData(entity.data, serverData);
        entity.data = resolvedData;
        entity.version = serverVersion;
        console.log(`🔄 Conflict resolved (merge): ${entityId}`);
        break;
    }

    entity.timestamp = Date.now();
    entity.hash = this.hashData(resolvedData);

    const resolution: ConflictResolution = {
      strategy,
      clientVersion,
      serverVersion,
      resolvedData,
    };

    this.conflictLog.push(resolution);
    if (this.conflictLog.length > 100) {
      this.conflictLog.shift();
    }

    return resolution;
  }

  /**
   * Merge two data objects intelligently
   */
  private static mergeData(clientData: any, serverData: any): any {
    const merged = { ...serverData };

    // Merge non-conflicting fields
    for (const key in clientData) {
      if (!(key in serverData)) {
        merged[key] = clientData[key];
      }
    }

    return merged;
  }

  /**
   * Hash data for change detection
   */
  private static hashData(data: any): string {
    const json = JSON.stringify(data);
    let hash = 0;

    for (let i = 0; i < json.length; i++) {
      const char = json.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    return Math.abs(hash).toString(16);
  }

  /**
   * Get entity version
   */
  static getEntityVersion(entityId: string): EntityVersion | null {
    return this.entities.get(entityId) || null;
  }

  /**
   * Check if entity has changed
   */
  static hasEntityChanged(entityId: string, data: any): boolean {
    const entity = this.entities.get(entityId);
    if (!entity) return true;

    const newHash = this.hashData(data);
    return newHash !== entity.hash;
  }

  /**
   * Get conflict log
   */
  static getConflictLog(): ConflictResolution[] {
    return [...this.conflictLog];
  }

  /**
   * Get sync statistics
   */
  static getStats() {
    return {
      trackedEntities: this.entities.size,
      pendingDeltas: this.pendingDeltas.size,
      totalConflicts: this.conflictLog.length,
      recentConflicts: this.conflictLog.slice(-10),
    };
  }

  /**
   * Clear entity tracking
   */
  static clearEntity(entityId: string): void {
    this.entities.delete(entityId);
    this.pendingDeltas.delete(entityId);
  }

  /**
   * Clear all tracking
   */
  static clearAll(): void {
    this.entities.clear();
    this.pendingDeltas.clear();
    this.conflictLog = [];
    console.log('🧹 Cleared all entity sync tracking');
  }
}

export default EntitySyncManager;
